import ApiService, { Params } from '@/api/ApiService';
import { CreateEnrichmentFieldPayload } from '@/constants/EnrichmentFields';

export default {
  create(payload: any) {
    return ApiService.post(`/surveys`, payload);
  },
  createStep(id: string, index: number, payload: any) {
    return ApiService.post(`/surveys/${id}/steps/${index}`, payload);
  },
  replaceWithDefault(id: string, stepId: string, payload: any) {
    return ApiService.patch(
      `/surveys/${id}/steps/${stepId}/replace-with-default`,
      payload,
    );
  },
  findAll(params: Params) {
    const config = {
      params,
    };
    return ApiService.get(`/surveys`, config);
  },
  findAllSamples() {
    return ApiService.get(`/surveys/samples`);
  },
  createSample(id: string, params: Params) {
    const config = {
      params,
    };
    return ApiService.post(`/surveys/${id}/samples`, {}, config);
  },
  overview(params: Params) {
    const config = {
      params,
    };
    return ApiService.get(`/surveys/overview`, config);
  },
  findOne(id: string) {
    return ApiService.get(`/surveys/${id}`);
  },
  findOneByShareableLinkId(id: string) {
    return ApiService.get(`/surveys/${id}/shareable-link`);
  },
  duplicateOne(id: string, payload: any, params?: Params) {
    return ApiService.post(`/surveys/${id}/duplicate`, payload, { params });
  },
  update(id: string, payload: any) {
    return ApiService.patch(`/surveys/${id}`, payload);
  },
  upgrade(id: string, params: Params) {
    const config = {
      params,
    };
    return ApiService.patch(`/surveys/${id}/upgrade`, {}, config);
  },
  adminUpgrade(id: string, params: Params) {
    const config = {
      params,
    };
    return ApiService.patch(`/surveys/${id}/manual-upgrade`, {}, config);
  },
  uploadImage(id: string, payload: any, params: Params) {
    const config = {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
    };
    return ApiService.post(`/surveys/${id}/upload`, payload, config);
  },
  remove(id: string) {
    return ApiService.delete(`/surveys/${id}`);
  },
  updatePath(surveyId: string, payload: any) {
    return ApiService.patch(`/surveys/${surveyId}/path`, payload);
  },
  export(surveyId: string, params: Params) {
    const config = { params };
    return ApiService.get(`surveys/${surveyId}/export`, config);
  },
  updateNotificationSettings(surveyId: string, payload: any) {
    return ApiService.patch(`/surveys/${surveyId}/notifications`, payload);
  },
  steps: {
    remove(id: string, stepId: string) {
      return ApiService.delete(`/surveys/${id}/steps/${stepId}`);
    },
    duplicate(surveyId: string, stepId: string) {
      return ApiService.post(`/surveys/${surveyId}/steps/${stepId}/duplicate`);
    },
    replace(id: string, stepId: string, payload: any) {
      return ApiService.put(`/surveys/${id}/steps/${stepId}`, payload);
    },
    updateOrder(id: string, payload: any) {
      return ApiService.put(`/surveys/${id}/steps`, payload);
    },
    update(id: string, stepId: string, payload: any) {
      return ApiService.patch(`/surveys/${id}/steps/${stepId}`, payload);
    },
    updateImage(id: string, stepId: string, payload: any) {
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };
      return ApiService.post(
        `/surveys/${id}/steps/${stepId}/image`,
        payload,
        config,
      );
    },
  },
  members: {
    add(surveyId: string, payload: any) {
      return ApiService.post(`/surveys/${surveyId}/members`, payload);
    },
    addByShareableLink(surveyId: string) {
      return ApiService.post(`/surveys/${surveyId}/members/shareable-link`);
    },
    update(surveyId: string, memberId: string, payload: any) {
      return ApiService.patch(
        `/surveys/${surveyId}/members/${memberId}`,
        payload,
      );
    },
    remove(surveyId: string, memberId: string) {
      return ApiService.delete(`/surveys/${surveyId}/members/${memberId}`);
    },
  },
  googleSheets: {
    getAuthPortal(surveyId: string) {
      return ApiService.post(`/surveys/${surveyId}/google-sheets/`);
    },
    addIntegration(surveyId: string, params: Params) {
      const config = { params };
      return ApiService.patch(
        `/surveys/${surveyId}/google-sheets/`,
        {},
        config,
      );
    },
    deleteIntegration(surveyId: string) {
      return ApiService.delete(`/surveys/${surveyId}/google-sheets/`);
    },
    refreshIntegration(surveyId: string) {
      return ApiService.post(`/surveys/${surveyId}/google-sheets/refresh`);
    },
  },
  multipleChoiceStepAnswers: {
    addImages(surveyId: string, stepId: string, payload: any) {
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };
      return ApiService.post(
        `/surveys/${surveyId}/steps/multiple-choice-answers/${stepId}/images`,
        payload,
        config,
      );
    },
    create(surveyId: string, stepId: string, payload: any) {
      return ApiService.post(
        `/surveys/${surveyId}/steps/multiple-choice-answers/${stepId}/`,
        payload,
      );
    },
    update(surveyId: string, stepId: string, answerId: string, payload: any) {
      return ApiService.patch(
        `/surveys/${surveyId}/steps/multiple-choice-answers/${stepId}/${answerId}`,
        payload,
      );
    },
    remove(surveyId: string, stepId: string, answerId: string) {
      return ApiService.delete(
        `/surveys/${surveyId}/steps/multiple-choice-answers/${stepId}/${answerId}`,
      );
    },
    aggregateForSegment(surveyId: string, params: Params) {
      const config = {
        params,
      };
      return ApiService.get(
        `/surveys/${surveyId}/steps/multiple-choice-answers/aggregate-responses-for-segment`,
        config,
      );
    },
    findAll(surveyId: string, stepId: string, params: Params) {
      const config = {
        params,
      };
      return ApiService.get(
        `/surveys/${surveyId}/steps/multiple-choice-answers/${stepId}`,
        config,
      );
    },
    findAllSegments(
      surveyId: string,
      stepId: string,
      params: Params,
      paginationParams: Params,
    ) {
      const config = {
        params: paginationParams,
      };
      return ApiService.post(
        `/surveys/${surveyId}/steps/multiple-choice-answers/${stepId}/find-all-segments`,
        params,
        config,
      );
    },
  },
  ratingScaleStepAnswers: {
    findAll(surveyId: string, stepId: string, params: Params) {
      const config = {
        params,
      };
      return ApiService.get(
        `/surveys/${surveyId}/steps/rating-scale-answers/${stepId}`,
        config,
      );
    },
    findAllSegments(
      surveyId: string,
      stepId: string,
      params: Params,
      paginationParams: Params,
    ) {
      const config = {
        params: paginationParams,
      };
      return ApiService.post(
        `/surveys/${surveyId}/steps/rating-scale-answers/${stepId}/find-all-segments`,
        params,
        config,
      );
    },
    aggregateForSegment(surveyId: string, params: Params) {
      const config = {
        params,
      };
      return ApiService.get(
        `/surveys/${surveyId}/steps/rating-scale-answers/aggregate-responses-for-segment`,
        config,
      );
    },
  },
  numberResponseStepAnswers: {
    findAll(surveyId: string, stepId: string, params: Params) {
      const config = {
        params,
      };
      return ApiService.get(
        `/surveys/${surveyId}/steps/number-response-answers/${stepId}`,
        config,
      );
    },
  },
  profileRankStep: {
    findAllProfiles(surveyId: string, stepId: string, params: Params) {
      return ApiService.get(
        `/surveys/${surveyId}/steps/${stepId}/profile-rank/profiles`,
        { params },
      );
    },
    simulateMarket(
      surveyId: string,
      stepId: string,
      payload: any,
      params: Params,
    ) {
      return ApiService.post(
        `surveys/${surveyId}/steps/${stepId}/profile-rank/market-simulator`,
        payload,
        { params },
      );
    },
    findAllScenarios(surveyId: string, stepId: string) {
      return ApiService.get(
        `surveys/${surveyId}/steps/${stepId}/profile-rank/market-simulator/scenarios`,
      );
    },
    createScenario(
      surveyId: string,
      stepId: string,
      payload: {
        name: string;
        profiles: { options: { categoryId: string; optionId: string }[] }[];
      },
    ) {
      return ApiService.post(
        `surveys/${surveyId}/steps/${stepId}/profile-rank/market-simulator/scenarios`,
        payload,
      );
    },
    removeScenario(surveyId: string, stepId: string, scenarioId: string) {
      return ApiService.delete(
        `surveys/${surveyId}/steps/${stepId}/profile-rank/market-simulator/scenarios/${scenarioId}`,
      );
    },
  },
  finish: {
    create(surveyId: string, payload: any) {
      return ApiService.post(`surveys/${surveyId}/finishes`, payload);
    },
    update(surveyId: string, finishId: string, payload: any) {
      return ApiService.patch(
        `surveys/${surveyId}/finishes/${finishId}`,
        payload,
      );
    },
    remove(surveyId: string, finishId: string) {
      return ApiService.delete(`surveys/${surveyId}/finishes/${finishId}`);
    },
  },
  enrichmentFields: {
    categoricalResults: {
      findAll(surveyId: string, enrichmentFieldId: string, params: Params) {
        return ApiService.get(
          `/surveys/${surveyId}/enrichment-fields/${enrichmentFieldId}/category-results`,
          { params },
        );
      },
      findAllSegments(
        surveyId: string,
        enrichmentFieldId: string,
        params: Params,
        paginationParams: Params,
      ) {
        return ApiService.post(
          `/surveys/${surveyId}/enrichment-fields/${enrichmentFieldId}/category-results/multiple-segments`,
          params,
          {
            params: paginationParams,
          },
        );
      },
    },
    remove(surveyId: string, enrichmentFieldId: string) {
      return ApiService.delete(
        `/surveys/${surveyId}/enrichment-fields/${enrichmentFieldId}`,
      );
    },
    create(
      surveyId: string,
      payload: { enrichmentFields: CreateEnrichmentFieldPayload[] },
    ) {
      return ApiService.post(`/surveys/${surveyId}/enrichment-fields`, payload);
    },
  },
};
